import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import {
  collection, getDocs, query, where, doc, getDoc,
  updateDoc, serverTimestamp
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Must match the companySlug helper in functions/index.js
const companySlug = (name) => String(name || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const LEVELS = [
  { min: 4,  max: 7,  label: 'AI Newcomer' },
  { min: 8,  max: 11, label: 'Early Explorer' },
  { min: 12, max: 15, label: 'Developing Practitioner' },
  { min: 16, max: 18, label: 'Active Practitioner' },
  { min: 19, max: 20, label: 'AI Champion' },
];

const getLevel = (score) => LEVELS.find(l => score >= l.min && score <= l.max) || LEVELS[0];

const levelColors = {
  'AI Newcomer':            { background: '#fdecea', color: '#a32d2d' },
  'Early Explorer':         { background: '#fef5e4', color: '#854f0b' },
  'Developing Practitioner':{ background: '#eaf3de', color: '#3b6d11' },
  'Active Practitioner':    { background: '#e1f5ee', color: '#0f6e56' },
  'AI Champion':            { background: '#e6f1fb', color: '#185fa5' },
};

const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export default function CompanyAdminDashboard({ user }) {
  const [adminProfile, setAdminProfile] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [filters, setFilters] = useState({ department: '', jobTitle: '', level: '', name: '', cohort: '', exam: '', access: '', post: '' });
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [journeyBase, setJourneyBase] = useState('registered');
  const [selected, setSelected] = useState({});
  const [bulkDayRange, setBulkDayRange] = useState({ dayStart: '', dayEnd: '' });
  const [bulkVideoDays, setBulkVideoDays] = useState([]);
  const [companyPools, setCompanyPools] = useState({});
  const [addingCode, setAddingCode] = useState({});
  const [newCodeForm, setNewCodeForm] = useState({});
  const [creatingCode, setCreatingCode] = useState(false);
  const [emailEdit, setEmailEdit] = useState(null);
  const [emailEditValue, setEmailEditValue] = useState('');
  const [emailEditSaving, setEmailEditSaving] = useState(false);
  const [detailsEdit, setDetailsEdit] = useState(null);
  const [detailsEditValue, setDetailsEditValue] = useState({ department: '', jobTitle: '' });
  const [detailsEditSaving, setDetailsEditSaving] = useState(false);
  const [wins, setWins] = useState([]);
  const [inviteRecs, setInviteRecs] = useState([]);
  const [resetBusy, setResetBusy] = useState({});
  const [rateInput, setRateInput] = useState('');
  const [savingRate, setSavingRate] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const adminSnap = await getDoc(doc(db, 'companyAdmins', user.email));
        if (!adminSnap.exists()) { setLoading(false); return; }
        const admin = adminSnap.data();
        const codes = admin.companyCodes || (admin.companyCode ? [admin.companyCode] : []);
        setAdminProfile({ ...admin, companyCodes: codes });

        const engSnaps = await Promise.all(codes.map(code => getDoc(doc(db, 'engagements', code))));
        const engList = engSnaps.filter(s => s.exists()).map(s => ({ id: s.id, ...s.data() }));
        setEngagements(engList);

        // Invite records for the invited-vs-joined stat.
        try {
          const invSnaps = await Promise.all(codes.map(code =>
            getDocs(query(collection(db, 'invites'), where('companyCode', '==', code)))
          ));
          setInviteRecs(invSnaps.flatMap(snap => snap.docs.map(d => d.data())));
        } catch { /* stat is optional; dashboard still works */ }

        // Wins for the ROI report. Company-wide, so wins posted under any of the
        // company's codes roll up; the per-code pass catches older wins.
        try {
          const companyNames = [...new Set(engList.map(e => e.company).filter(Boolean))];
          const winSnaps = await Promise.all([
            ...companyNames.map(name => getDocs(query(collection(db, 'wins'), where('company', '==', name))).catch(() => null)),
            ...codes.map(code => getDocs(query(collection(db, 'wins'), where('companyCode', '==', code))).catch(() => null)),
          ]);
          const byId = new Map();
          winSnaps.forEach(snap => snap && snap.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() })));
          setWins([...byId.values()]);
        } catch { /* ROI panel will just be empty */ }
        const engMap = {};
        engList.forEach(e => { engMap[e.id] = e; });

        const userSnaps = await Promise.all(
          codes.map(code => getDocs(query(collection(db, 'users'), where('companyCode', '==', code))))
        );
        const userMap = {};
        userSnaps.forEach(snap => snap.docs.forEach(d => { userMap[d.id] = { id: d.id, ...d.data() }; }));

        // Workbook progress and exam status come from the server, numbers
        // only. Company admins have no read access to workbook content by
        // design, so a getTeamProgress Cloud Function counts on the server
        // and returns counts. A failure here falls back to zeros rather
        // than blocking the dashboard.
        let progressMap = {};
        try {
          const getTeamProgress = httpsCallable(getFunctions(), 'getTeamProgress');
          const res = await getTeamProgress();
          progressMap = res.data?.participants || {};
        } catch (err) {
          console.error('getTeamProgress failed:', err);
        }

        // Load the shared seat pool for each company
        try {
          const companies = [...new Set(engList.map(e => e.company).filter(Boolean))];
          const poolSnaps = await Promise.all(companies.map(c => getDoc(doc(db, 'companies', companySlug(c)))));
          const pools = {};
          poolSnaps.forEach((snap, i) => { if (snap.exists()) pools[companySlug(companies[i])] = snap.data(); });
          setCompanyPools(pools);
        } catch { /* seat meter just will not display */ }

        const enriched = await Promise.all(Object.values(userMap).map(async (u) => {
          try {
            const aSnap = await getDoc(doc(db, 'assessments', u.id));
            const a = aSnap.exists() ? aSnap.data() : null;

            const prog = progressMap[u.id] || { days: {}, daysStarted: 0, daysCompleted: 0, sectionsCompleted: 0, exam: { taken: false, passed: false, score: null, attempts: 0 } };

            const dayStart = u.dayRangeStart || 1;
            const dayEnd = u.dayRangeEnd || 10;
            const assignedDays = Math.max(0, dayEnd - dayStart + 1);

            // Only count completions within the assigned range
            let completedDays = 0;
            for (let d = dayStart; d <= dayEnd; d++) {
              if (prog.days[d]?.complete) completedDays++;
            }

            return {
              ...u,
              preScore: a?.pre?.scores?.total || null,
              postScore: a?.post?.scores?.total || null,
              completedDays,
              assignedDays,
              daysStarted: prog.daysStarted,
              sectionsCompleted: prog.sectionsCompleted,
              exam: prog.exam,
              postAssessmentUnlocked: u.postAssessmentUnlocked || false,
              videoAccess: Array.isArray(u.videoAccess) ? u.videoAccess : [],
              cohortVideosEnabled: !!engMap[u.companyCode]?.videosEnabled,
            };
          } catch { return { ...u, preScore: null, postScore: null, completedDays: 0, assignedDays: 10, daysStarted: 0, sectionsCompleted: 0, exam: { taken: false, passed: false, score: null, attempts: 0 }, videoAccess: [], cohortVideosEnabled: false }; }
        }));

        setParticipants(enriched);
      } catch (err) {
        console.error('CompanyAdminDashboard load error:', err);
      }
      setLoading(false);
    };
    load();
  }, [user.email]);

  const showMsg = (type, text) => {
    setActionMsg({ type, text });
    setTimeout(() => setActionMsg(null), 3500);
  };

  const handleUnlockPost = async (participantId) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', participantId), {
        postAssessmentUnlocked: true,
        postAssessmentUnlockedAt: serverTimestamp(),
      });
      setParticipants(prev => prev.map(p => p.id === participantId ? { ...p, postAssessmentUnlocked: true } : p));
      showMsg('success', 'Post assessment unlocked.');
    } catch (err) { showMsg('error', err.message); }
    setSaving(false);
  };

  const handleExtendAccess = async (participantId, days) => {
    setSaving(true);
    try {
      const p = participants.find(p => p.id === participantId);
      const base = p.accessExpiresAt?.toDate ? p.accessExpiresAt.toDate() : new Date(p.accessExpiresAt || Date.now());
      const newExpiry = new Date(Math.max(base, new Date()).getTime() + days * 24 * 60 * 60 * 1000);
      await updateDoc(doc(db, 'users', participantId), { accessExpiresAt: newExpiry });
      setParticipants(prev => prev.map(p => p.id === participantId ? { ...p, accessExpiresAt: newExpiry } : p));
      showMsg('success', `Access extended by ${days} days.`);
    } catch (err) { showMsg('error', err.message); }
    setSaving(false);
  };

  const handleEndAccess = async (participantId) => {
    if (!window.confirm('End access for this participant?')) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', participantId), { accessExpiresAt: new Date() });
      setParticipants(prev => prev.map(p => p.id === participantId ? { ...p, accessExpiresAt: new Date() } : p));
      showMsg('success', 'Access ended.');
    } catch (err) { showMsg('error', err.message); }
    setSaving(false);
  };

  const openEmailEdit = (p) => { setEmailEdit(p); setEmailEditValue(p.email || ''); };

  // Send a branded reset email on a team member's behalf.
  const handleSendReset = async (p) => {
    if (!p.email) return;
    if (!window.confirm(`Send a password reset email to ${p.email}?`)) return;
    setResetBusy(prev => ({ ...prev, [p.id]: true }));
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'sendPasswordReset');
      const res = await fn({ email: p.email });
      const d = res.data || {};
      showMsg(d.found === false ? 'error' : 'success', d.message || `Reset email sent to ${p.email}.`);
    } catch (err) {
      showMsg('error', err.message || 'Could not send the reset email.');
    }
    setResetBusy(prev => ({ ...prev, [p.id]: false }));
  };

  const handleSaveEmail = async () => {
    const newEmail = emailEditValue.trim().toLowerCase();
    if (!newEmail) { showMsg('error', 'Enter an email address.'); return; }
    setEmailEditSaving(true);
    try {
      const fn = httpsCallable(getFunctions(), 'adminUpdateUserEmail');
      await fn({ targetUid: emailEdit.id, newEmail });
      setParticipants(prev => prev.map(p => p.id === emailEdit.id ? { ...p, email: newEmail } : p));
      showMsg('success', `Email updated to ${newEmail}.`);
      setEmailEdit(null);
    } catch (err) {
      showMsg('error', err.message || 'Could not update the email.');
    }
    setEmailEditSaving(false);
  };

  const openDetailsEdit = (p) => {
    setDetailsEdit(p);
    setDetailsEditValue({ department: p.department || '', jobTitle: p.jobTitle || '' });
  };

  const handleSaveDetails = async () => {
    const department = detailsEditValue.department.trim();
    const jobTitle = detailsEditValue.jobTitle.trim();
    setDetailsEditSaving(true);
    try {
      await updateDoc(doc(db, 'users', detailsEdit.id), { department, jobTitle });
      setParticipants(prev => prev.map(p => p.id === detailsEdit.id ? { ...p, department, jobTitle } : p));
      showMsg('success', 'Department and job title updated.');
      setDetailsEdit(null);
    } catch (err) {
      showMsg('error', err.message || 'Could not save the changes.');
    }
    setDetailsEditSaving(false);
  };

  const formatDate = (val) => {
    if (!val) return '—';
    try { return (val.toDate ? val.toDate() : new Date(val)).toLocaleDateString(); } catch { return '—'; }
  };

  const handleCreateCode = async (parentEng) => {
    const form = newCodeForm[parentEng.id] || {};
    const code = (form.code || '').toUpperCase().trim();
    if (!code) { showMsg('error', 'Enter a code first.'); return; }
    setCreatingCode(true);
    try {
      const createCompanyCode = httpsCallable(getFunctions(), 'createCompanyCode');
      await createCompanyCode({
        parentCode: parentEng.id,
        newCode: code,
        expiresAt: form.expiresAt || null,
      });
      setEngagements(prev => [...prev, {
        id: code,
        code,
        company: parentEng.company,
        parentCode: parentEng.id,
        expiresAt: form.expiresAt ? new Date(form.expiresAt) : null,
        postAssessmentUnlocked: false,
        videosEnabled: false,
      }]);
      setAdminProfile(prev => prev ? { ...prev, companyCodes: [...new Set([...(prev.companyCodes || []), code])] } : prev);
      setNewCodeForm(prev => ({ ...prev, [parentEng.id]: {} }));
      setAddingCode(prev => ({ ...prev, [parentEng.id]: false }));
      showMsg('success', `Code ${code} created. Share it with the team that should register under it.`);
    } catch (err) {
      if (err.code === 'functions/already-exists') {
        showMsg('error', 'That code already exists. Pick a different one.');
      } else if (err.code === 'functions/invalid-argument') {
        showMsg('error', 'Code must be 4 to 30 characters, letters, numbers, and hyphens only.');
      } else {
        showMsg('error', 'Could not create the code. Please try again.');
      }
    }
    setCreatingCode(false);
  };

  const isAccessActive = (p) => {
    if (!p.accessExpiresAt) return true;
    try { return (p.accessExpiresAt.toDate ? p.accessExpiresAt.toDate() : new Date(p.accessExpiresAt)) > new Date(); }
    catch { return true; }
  };

  const toggleSelect = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const applyDayRange = async () => {
    const targets = getTargets();
    if (!targets.length) { showMsg('error', 'Select at least one participant.'); return; }
    if (!bulkDayRange.dayStart || !bulkDayRange.dayEnd) { showMsg('error', 'Set both a start and end day.'); return; }
    setSaving(true);
    try {
      await Promise.all(targets.map(p => updateDoc(doc(db, 'users', p.id), {
        dayRangeStart: parseInt(bulkDayRange.dayStart),
        dayRangeEnd: parseInt(bulkDayRange.dayEnd),
      })));
      const newStart = parseInt(bulkDayRange.dayStart);
      const newEnd = parseInt(bulkDayRange.dayEnd);
      setParticipants(prev => prev.map(p =>
        targets.find(t => t.id === p.id)
          ? { ...p, dayRangeStart: newStart, dayRangeEnd: newEnd, assignedDays: Math.max(0, newEnd - newStart + 1) }
          : p
      ));
      setSelected({});
      showMsg('success', `Day range updated for ${targets.length} participant${targets.length !== 1 ? 's' : ''}.`);
    } catch (err) { showMsg('error', err.message); }
    setSaving(false);
  };

  const toggleVideoDayChip = (day) => {
    setBulkVideoDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a, b) => a - b));
  };

  const applyVideoAccess = async () => {
    const targets = getTargets();
    if (!targets.length) { showMsg('error', 'Select at least one participant.'); return; }
    if (!bulkVideoDays.length) { showMsg('error', 'Pick at least one day to grant.'); return; }
    setSaving(true);
    try {
      await Promise.all(targets.map(p => updateDoc(doc(db, 'users', p.id), { videoAccess: bulkVideoDays })));
      setParticipants(prev => prev.map(p => targets.find(t => t.id === p.id) ? { ...p, videoAccess: bulkVideoDays } : p));
      setSelected({});
      setBulkVideoDays([]);
      showMsg('success', `Video access granted for Day ${bulkVideoDays.join(', Day ')} to ${targets.length} participant${targets.length !== 1 ? 's' : ''}.`);
    } catch (err) { showMsg('error', err.message); }
    setSaving(false);
  };

  const clearVideoAccess = async () => {
    const targets = getTargets();
    if (!targets.length) { showMsg('error', 'Select at least one participant.'); return; }
    setSaving(true);
    try {
      await Promise.all(targets.map(p => updateDoc(doc(db, 'users', p.id), { videoAccess: [] })));
      setParticipants(prev => prev.map(p => targets.find(t => t.id === p.id) ? { ...p, videoAccess: [] } : p));
      setSelected({});
      setBulkVideoDays([]);
      showMsg('success', `Video access cleared for ${targets.length} participant${targets.length !== 1 ? 's' : ''}.`);
    } catch (err) { showMsg('error', err.message); }
    setSaving(false);
  };

  const handleBulkUnlockPost = async (targets) => {
    if (!targets.length) return;
    setSaving(true);
    try {
      await Promise.all(targets.map(p => updateDoc(doc(db, 'users', p.id), {
        postAssessmentUnlocked: true,
        postAssessmentUnlockedAt: serverTimestamp(),
      })));
      setParticipants(prev => prev.map(p => targets.find(t => t.id === p.id) ? { ...p, postAssessmentUnlocked: true } : p));
      setSelected({});
      showMsg('success', `Post assessment unlocked for ${targets.length} participant${targets.length !== 1 ? 's' : ''}.`);
    } catch (err) { showMsg('error', err.message); }
    setSaving(false);
  };

  const handleBulkEndAccess = async (targets) => {
    if (!targets.length) return;
    if (!window.confirm(`End access for ${targets.length} participant${targets.length !== 1 ? 's' : ''}?`)) return;
    setSaving(true);
    try {
      await Promise.all(targets.map(p => updateDoc(doc(db, 'users', p.id), { accessExpiresAt: new Date() })));
      setParticipants(prev => prev.map(p => targets.find(t => t.id === p.id) ? { ...p, accessExpiresAt: new Date() } : p));
      setSelected({});
      showMsg('success', `Access ended for ${targets.length} participant${targets.length !== 1 ? 's' : ''}.`);
    } catch (err) { showMsg('error', err.message); }
    setSaving(false);
  };

  const handleSendReminders = async () => {
    const targets = getTargets();
    if (!targets.length) { showMsg('error', 'Select at least one participant.'); return; }
    setSaving(true);
    try {
      const fn = httpsCallable(getFunctions(), 'sendReminders');
      const res = await fn({ uids: targets.map(p => p.id) });
      const d = res.data || {};
      showMsg(d.failedCount ? 'error' : 'success', `Reminder sent to ${d.sentCount}.${d.failedCount ? ` ${d.failedCount} failed.` : ''}`);
      setSelected({});
    } catch (err) { showMsg('error', err.message || 'Could not send reminders.'); }
    setSaving(false);
  };

  const handleSaveRate = async () => {
    const current = (() => { const e = engagements.find(x => typeof x.hourlyRate === 'number'); return e ? e.hourlyRate : 50; })();
    const rate = parseFloat(rateInput !== '' ? rateInput : current);
    if (isNaN(rate) || rate < 0) { showMsg('error', 'Enter a valid hourly rate.'); return; }
    setSavingRate(true);
    try {
      const codes = adminProfile?.companyCodes || [];
      await Promise.all(codes.map(code => updateDoc(doc(db, 'engagements', code), { hourlyRate: rate })));
      setEngagements(prev => prev.map(e => ({ ...e, hourlyRate: rate })));
      setRateInput('');
      showMsg('success', `Hourly rate set to $${rate}.`);
    } catch (err) { showMsg('error', err.message || 'Could not save the rate.'); }
    setSavingRate(false);
  };

  const handleSort = (key) => {
    if (sortKey === key) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortKey(key); setSortDir('asc'); }
  };

  const handleExport = () => {
    import('xlsx').then(XLSX => {
      const targets = getTargets().length > 0 ? getTargets() : filtered;
      const rows = targets.map(p => ({
        'Name': p.displayName || '',
        'Email': p.email || '',
        'Department': p.department || '',
        'Job Title': p.jobTitle || '',
        'Pre Score': p.preScore != null ? p.preScore : 'Not taken',
        'Post Score': p.postScore != null ? p.postScore : 'Not taken',
        'Score Change': (p.preScore != null && p.postScore != null) ? p.postScore - p.preScore : 'N/A',
        'Level': (p.postScore || p.preScore) ? getLevel(p.postScore || p.preScore).label : '',
        'Workbooks Completed': p.completedDays,
        'Workbook Status': p.completedDays >= (p.assignedDays ?? 10) ? 'Complete' : (p.sectionsCompleted > 0 ? 'In progress' : 'Not started'),
        'Final Exam': !p.exam?.taken ? 'Not taken' : (p.exam.passed ? `Passed (${p.exam.score != null ? p.exam.score : ''})`.trim() : `In progress, ${p.exam.attempts} attempt${p.exam.attempts !== 1 ? 's' : ''}`),
        'Video Access': p.cohortVideosEnabled ? 'Cohort On' : (p.videoAccess && p.videoAccess.length ? p.videoAccess.map(d => `D${d}`).join(', ') : 'None'),
        'Post Assessment': p.postAssessmentUnlocked ? 'Unlocked' : 'Locked',
        'Access Expires': formatDate(p.accessExpiresAt),
        'Access': isAccessActive(p) ? 'Active' : 'Ended',
        'Cohort': p.companyCode || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Participants');
      XLSX.writeFile(wb, `Team-AI-Readiness-${new Date().toISOString().slice(0, 10)}.xlsx`);
    });
  };

  if (loading) return <div style={s.loading}>Loading dashboard...</div>;
  if (!adminProfile) return <div style={s.error}>Your account is not configured as a company admin. Contact your program coordinator.</div>;

  const departments = [...new Set(participants.map(p => p.department).filter(Boolean))];
  const jobTitles = [...new Set(participants.map(p => p.jobTitle).filter(Boolean))];

  const filtered = participants
    .filter(p => {
      if (filters.name && !(p.displayName || '').toLowerCase().includes(filters.name.toLowerCase()) && !(p.email || '').toLowerCase().includes(filters.name.toLowerCase())) return false;
      if (filters.department && p.department !== filters.department) return false;
      if (filters.jobTitle && p.jobTitle !== filters.jobTitle) return false;
      if (filters.level) {
        const lvl = (p.postScore || p.preScore) ? getLevel(p.postScore || p.preScore).label : '';
        if (lvl !== filters.level) return false;
      }
      if (filters.cohort && p.companyCode !== filters.cohort) return false;
      if (filters.exam) {
        const ex = p.exam || { taken: false, passed: false };
        if (filters.exam === 'passed' && !ex.passed) return false;
        if (filters.exam === 'inprogress' && !(ex.taken && !ex.passed)) return false;
        if (filters.exam === 'nottaken' && ex.taken) return false;
      }
      if (filters.access) {
        const active = isAccessActive(p);
        if (filters.access === 'active' && !active) return false;
        if (filters.access === 'ended' && active) return false;
      }
      if (filters.post) {
        if (filters.post === 'unlocked' && !p.postAssessmentUnlocked) return false;
        if (filters.post === 'locked' && p.postAssessmentUnlocked) return false;
      }
      return true;
    })
    .sort((a, b) => {
      let av, bv;
      if (sortKey === 'name') { av = a.displayName || ''; bv = b.displayName || ''; }
      else if (sortKey === 'days') { av = a.completedDays; bv = b.completedDays; }
      else if (sortKey === 'pre') { av = a.preScore || 0; bv = b.preScore || 0; }
      else if (sortKey === 'post') { av = a.postScore || 0; bv = b.postScore || 0; }
      else if (sortKey === 'change') { av = (a.preScore && a.postScore) ? a.postScore - a.preScore : -99; bv = (b.preScore && b.postScore) ? b.postScore - b.preScore : -99; }
      else if (sortKey === 'dept') { av = a.department || ''; bv = b.department || ''; }
      else if (sortKey === 'jobTitle') { av = a.jobTitle || ''; bv = b.jobTitle || ''; }
      else if (sortKey === 'cohort') { av = a.companyCode || ''; bv = b.companyCode || ''; }
      else if (sortKey === 'level') { av = a.postScore || a.preScore || 0; bv = b.postScore || b.preScore || 0; }
      else if (sortKey === 'exam') {
        const rank = (p) => { const ex = p.exam || {}; return ex.passed ? 2 : (ex.taken ? 1 : 0); };
        av = rank(a) * 1000 + (a.exam?.score || 0); bv = rank(b) * 1000 + (b.exam?.score || 0);
      }
      else if (sortKey === 'videos') {
        const vrank = (p) => p.cohortVideosEnabled ? 99 : (Array.isArray(p.videoAccess) ? p.videoAccess.length : 0);
        av = vrank(a); bv = vrank(b);
      }
      else if (sortKey === 'accessExpires') {
        const ms = (p) => { try { return p.accessExpiresAt ? (p.accessExpiresAt.toDate ? p.accessExpiresAt.toDate() : new Date(p.accessExpiresAt)).getTime() : 0; } catch { return 0; } };
        av = ms(a); bv = ms(b);
      }
      else if (sortKey === 'postAssessment') { av = a.postAssessmentUnlocked ? 1 : 0; bv = b.postAssessmentUnlocked ? 1 : 0; }
      else { av = a[sortKey] || ''; bv = b[sortKey] || ''; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const avgPre = participants.filter(p => p.preScore).reduce((a, p) => a + p.preScore, 0) / (participants.filter(p => p.preScore).length || 1);
  const avgPost = participants.filter(p => p.postScore).reduce((a, p) => a + p.postScore, 0) / (participants.filter(p => p.postScore).length || 1);
  const bothDone = participants.filter(p => p.preScore && p.postScore).length;
  const showCohortCol = (adminProfile.companyCodes || []).length > 1;

  // Invited vs joined, scoped to the cohort filter when one is picked.
  // Joined only counts accounts in the matching cohort so it can never
  // exceed the registered count.
  const participantEmailSet = new Set(
    participants
      .filter(p => !filters.cohort || p.companyCode === filters.cohort)
      .map(p => (p.email || '').toLowerCase())
      .filter(Boolean)
  );
  const relevantInvites = filters.cohort ? inviteRecs.filter(inv => inv.companyCode === filters.cohort) : inviteRecs;
  // Count distinct people, not invite rows: the same person invited under two
  // codes is still one person.
  const invitedEmails = new Set(relevantInvites.map(inv => (inv.email || '').toLowerCase()).filter(Boolean));
  const invitedCount = invitedEmails.size;
  const joinedCount = [...invitedEmails].filter(e => participantEmailSet.has(e)).length;

  // "Where your team is" journey snapshot — follows the filters/search below.
  const journeyTotal = (journeyBase === 'invited' && invitedCount > 0 ? invitedCount : filtered.length) || 1;
  const journey = [
    ...(invitedCount > 0 ? [{ label: 'Invited', count: invitedCount, gold: false }] : []),
    { label: 'Registered', count: filtered.length, gold: false },
    { label: 'Snapshot taken', count: filtered.filter(p => p.preScore != null).length, gold: false },
    { label: 'Workbooks in progress', count: filtered.filter(p => p.daysStarted > 0 && p.completedDays < (p.assignedDays ?? 10)).length, gold: false },
    { label: 'Workbooks complete', count: filtered.filter(p => (p.assignedDays ?? 10) > 0 && p.completedDays >= (p.assignedDays ?? 10)).length, gold: false },
    { label: 'Final exam in progress', count: filtered.filter(p => p.exam?.taken && !p.exam?.passed).length, gold: false },
    { label: 'Certified', count: filtered.filter(p => p.exam?.passed).length, gold: true },
  ];

  // Wins & ROI rollup.
  const rateEng = engagements.find(e => typeof e.hourlyRate === 'number');
  const hourlyRate = rateEng ? rateEng.hourlyRate : 50;
  const roiWins = wins.filter(w => w.annualizedHours > 0);
  const totalHours = Math.round(roiWins.reduce((a, w) => a + (w.annualizedHours || 0), 0));
  const totalDollars = Math.round(totalHours * hourlyRate);
  const catCounts = {};
  wins.forEach(w => { if (w.category) catCounts[w.category] = (catCounts[w.category] || 0) + 1; });
  const topCats = Object.entries(catCounts).sort((a, b) => b[1] - a[1]).slice(0, 3);
  const topWins = [...roiWins].sort((a, b) => (b.annualizedHours || 0) - (a.annualizedHours || 0)).slice(0, 3);

  const toggleSelectAll = () => {
    const allSel = filtered.every(p => selected[p.id]);
    const newSel = {};
    filtered.forEach(p => { newSel[p.id] = !allSel; });
    setSelected(newSel);
  };
  const getTargets = () => filtered.filter(p => selected[p.id]);
  const selectedCount = getTargets().length;

  const SortTh = ({ label, sortId }) => (
    <th style={s.th} onClick={() => handleSort(sortId)}>
      <span style={{ cursor: 'pointer', userSelect: 'none' }}>
        {label} {sortKey === sortId ? (sortDir === 'asc' ? '↑' : '↓') : ''}
      </span>
    </th>
  );

  const formatVideoAccess = (p) => {
    if (p.cohortVideosEnabled) return { label: 'Cohort On', color: '#1a8a4a' };
    const days = Array.isArray(p.videoAccess) ? [...p.videoAccess].sort((a, b) => a - b) : [];
    if (!days.length) return { label: 'None', color: '#3D3D3D' };
    let contiguous = true;
    for (let i = 1; i < days.length; i++) {
      if (days[i] !== days[i - 1] + 1) { contiguous = false; break; }
    }
    if (contiguous && days.length > 1) {
      return { label: `D${days[0]}–D${days[days.length - 1]}`, color: '#185fa5' };
    }
    return { label: days.map(d => `D${d}`).join(', '), color: '#185fa5' };
  };

  return (
    <div style={s.wrapper}>
      <div style={s.heading}>Team Dashboard</div>
      <div style={s.sub}>
        {[...new Set(engagements.map(e => e.company))].join(' / ')} — all participants across your engagement{engagements.length > 1 ? 's' : ''}.
      </div>

      {(() => {
        const warnings = engagements.filter(eng => {
          if (!eng.accessExpiresAt) return false;
          const d = eng.accessExpiresAt.toDate ? eng.accessExpiresAt.toDate() : new Date(eng.accessExpiresAt);
          const days = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
          return days > 0 && days <= 15;
        });
        if (warnings.length === 0) return null;
        return (
          <div style={{ background: '#fef5e4', border: '1.5px solid #f5d78a', padding: '14px 20px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#854f0b', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Access Expiring Soon</div>
            {warnings.map(eng => {
              const d = eng.accessExpiresAt.toDate ? eng.accessExpiresAt.toDate() : new Date(eng.accessExpiresAt);
              const days = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
              return (
                <div key={eng.id} style={{ fontSize: '13px', color: '#3D3D3D' }}>
                  Participant access for <strong>{eng.company}</strong> expires in <strong style={{ color: '#854f0b' }}>{days} day{days !== 1 ? 's' : ''}</strong> on {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}. Contact your program coordinator to extend access.
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Floats so it is visible from anywhere in a long roster. */}
      {actionMsg && (
        <div
          style={{
            position: 'fixed', bottom: '24px', right: '24px', zIndex: 9500, maxWidth: '380px',
            padding: '14px 18px', fontSize: '13px', lineHeight: 1.5,
            boxShadow: '0 8px 28px rgba(27,42,74,0.28)',
            background: actionMsg.type === 'error' ? '#fdecea' : '#eaf3de',
            color: actionMsg.type === 'error' ? '#a32d2d' : '#3b6d11',
            border: `1.5px solid ${actionMsg.type === 'error' ? '#e6c4c4' : '#c5ddaa'}`,
            fontFamily: 'DM Sans, sans-serif',
          }}
          onClick={() => setActionMsg(null)}
          title="Click to dismiss"
        >
          {actionMsg.text}
        </div>
      )}

      <div style={s.statsStrip}>
        {[
          { label: 'Total Participants', val: participants.length, sub: `${participants.filter(isAccessActive).length} active` },
          { label: 'Invited vs Joined', val: `${joinedCount} / ${invitedCount}`, sub: invitedCount ? `${Math.round((joinedCount / invitedCount) * 100)}% of ${filters.cohort || 'all'} invites joined` : 'No invites yet' },
          (() => {
            const pools = Object.values(companyPools);
            if (!pools.length) return { label: 'Seats', val: participants.length, sub: 'No cap set' };
            const used = pools.reduce((a, p) => a + (typeof p.seatsUsed === 'number' ? p.seatsUsed : 0), 0);
            const capped = pools.filter(p => typeof p.seatLimit === 'number');
            if (!capped.length) return { label: 'Seats', val: used, sub: 'No cap set' };
            const limit = capped.reduce((a, p) => a + p.seatLimit, 0);
            return { label: 'Seats', val: `${used} / ${limit}`, sub: used >= limit ? 'Pool full, contact your program coordinator for more' : `${limit - used} remaining across all codes` };
          })(),
          { label: 'Avg Pre Score', val: participants.filter(p => p.preScore).length > 0 ? avgPre.toFixed(1) : '—', sub: 'Out of 20' },
          { label: 'Avg Post Score', val: participants.filter(p => p.postScore).length > 0 ? avgPost.toFixed(1) : '—', sub: participants.filter(p => p.postScore).length > 0 ? `+${(avgPost - avgPre).toFixed(1)} avg gain` : 'Pending' },
          { label: 'Completed Both', val: bothDone, sub: `${Math.round((bothDone / (participants.length || 1)) * 100)}% completion` },
        ].map(stat => (
          <div key={stat.label} style={s.statCard}>
            <div style={s.statLabel}>{stat.label}</div>
            <div style={s.statVal}>{stat.val}</div>
            <div style={s.statSub}>{stat.sub}</div>
          </div>
        ))}
      </div>

      <div style={s.journeyCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div style={s.journeyTitle}>Where your team is</div>
          {invitedCount > 0 && (
            <div style={{ display: 'flex', gap: '6px' }}>
              {[['invited', '% of invited'], ['registered', '% of registered']].map(([k, lbl]) => (
                <button
                  key={k}
                  onClick={() => setJourneyBase(k)}
                  style={{
                    background: journeyBase === k ? '#1B2A4A' : '#F2F0EC',
                    color: journeyBase === k ? '#fff' : '#1B2A4A',
                    border: '1.5px solid #E8E6E2', padding: '5px 12px', fontSize: '11px',
                    fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                  }}
                >{lbl}</button>
              ))}
            </div>
          )}
        </div>
        {journey.map(stage => {
          const pct = Math.round((stage.count / journeyTotal) * 100);
          return (
            <div key={stage.label} style={s.journeyRow}>
              <div style={s.journeyLabel}>{stage.label}</div>
              <div style={s.journeyTrack}>
                <div style={{ ...s.journeyFill, width: `${Math.min(pct, 100)}%`, background: stage.gold ? '#D4A847' : '#1B2A4A' }} />
              </div>
              <div style={s.journeyCount}>{stage.count}{stage.label !== 'Invited' && <span style={s.journeyPct}> {pct}%</span>}</div>
            </div>
          );
        })}
      </div>

      <div style={s.roiPanel}>
        <div style={s.roiHead}>
          <div style={s.journeyTitle}>Wins &amp; ROI</div>
          <div style={s.rateBox}>
            <span style={s.rateLabel}>Hourly rate $</span>
            <input type="number" min="0" style={s.rateInput} value={rateInput !== '' ? rateInput : hourlyRate} onChange={e => setRateInput(e.target.value)} />
            <button style={s.rateSaveBtn} onClick={handleSaveRate} disabled={savingRate}>{savingRate ? '…' : 'Save'}</button>
          </div>
        </div>
        <div style={s.roiStats}>
          <div style={s.roiStat}><div style={s.roiStatNum}>{wins.length}</div><div style={s.roiStatLabel}>wins shared</div></div>
          <div style={s.roiStat}><div style={s.roiStatNum}>{totalHours.toLocaleString()}</div><div style={s.roiStatLabel}>hours / year saved</div></div>
          <div style={s.roiStat}><div style={{ ...s.roiStatNum, color: '#3b6d11' }}>${totalDollars.toLocaleString()}</div><div style={s.roiStatLabel}>estimated value / year</div></div>
        </div>
        <div style={s.roiNote}>Based on {roiWins.length} win{roiWins.length === 1 ? '' : 's'} with time logged, at ${hourlyRate}/hour. This is an estimate — adjust the rate for your team.</div>
        {topCats.length > 0 && (
          <div style={s.roiCats}>{topCats.map(([cat, n]) => <span key={cat} style={s.roiCatBadge}>{cat} · {n}</span>)}</div>
        )}
        {topWins.length > 0 && (
          <div style={s.roiHighlights}>
            <div style={s.roiHighlightsLabel}>Top wins by impact</div>
            {topWins.map(w => (
              <div key={w.id} style={s.roiHighlight}>
                <span style={s.roiHighlightHours}>~{w.annualizedHours} hrs/yr</span>
                <span style={s.roiHighlightText}>{w.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {engagements.map(eng => (
        <div key={eng.id}>
          <div style={s.engBar}>
            {[
              { label: 'Code', value: eng.id },
              { label: 'Company', value: eng.company },
              { label: 'Registration Closes', value: formatDate(eng.expiresAt) },
              { label: 'Post Assessment', value: eng.postAssessmentUnlocked ? 'Unlocked' : 'Locked', color: eng.postAssessmentUnlocked ? '#1a8a4a' : '#C0392B' },
              { label: 'Participants', value: participants.filter(p => p.companyCode === eng.id).length },
            ].map(({ label, value, color }) => (
              <div key={label} style={s.engField}>
                <span style={s.engLabel}>{label}</span>
                <span style={{ ...s.engValue, ...(color ? { color } : {}) }}>{value}</span>
              </div>
            ))}
            <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
              <button style={s.actionBtn} onClick={() => setAddingCode(prev => ({ ...prev, [eng.id]: !prev[eng.id] }))}>
                {addingCode[eng.id] ? 'Cancel' : '+ Add Code'}
              </button>
            </div>
          </div>
          {addingCode[eng.id] && (
            <div style={{ background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '14px 20px', marginBottom: '4px', display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={s.engLabel}>New Code</span>
                <input
                  style={{ border: '1.5px solid #E8E6E2', padding: '7px 12px', fontFamily: 'monospace', fontSize: '13px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', width: '190px', outline: 'none' }}
                  placeholder={`e.g. ${eng.id}-HR`}
                  value={newCodeForm[eng.id]?.code || ''}
                  onChange={e => setNewCodeForm(prev => ({ ...prev, [eng.id]: { ...(prev[eng.id] || {}), code: e.target.value.toUpperCase() } }))}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={s.engLabel}>Registration Closes (optional)</span>
                <input
                  type="date"
                  style={{ border: '1.5px solid #E8E6E2', padding: '7px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', outline: 'none' }}
                  value={newCodeForm[eng.id]?.expiresAt || ''}
                  onChange={e => setNewCodeForm(prev => ({ ...prev, [eng.id]: { ...(prev[eng.id] || {}), expiresAt: e.target.value } }))}
                />
              </div>
              <button
                style={{ background: '#1B2A4A', color: '#fff', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}
                disabled={creatingCode}
                onClick={() => handleCreateCode(eng)}
              >
                {creatingCode ? 'Creating...' : 'Create Code →'}
              </button>
              <div style={{ fontSize: '11px', color: '#8A8A8A', flexBasis: '100%' }}>
                Use this to give a department, cohort, or wave its own registration code. Everyone still counts against your shared seat pool.
              </div>
            </div>
          )}
        </div>
      ))}

      <div style={s.filterBar}>
        <select style={s.select} value={filters.department} onChange={e => setFilters(f => ({ ...f, department: e.target.value }))}>
          <option value="">All Departments</option>
          {departments.map(d => <option key={d}>{d}</option>)}
        </select>
        <select style={s.select} value={filters.jobTitle} onChange={e => setFilters(f => ({ ...f, jobTitle: e.target.value }))}>
          <option value="">All Job Titles</option>
          {jobTitles.map(j => <option key={j}>{j}</option>)}
        </select>
        <select style={s.select} value={filters.level} onChange={e => setFilters(f => ({ ...f, level: e.target.value }))}>
          <option value="">All Levels</option>
          {LEVELS.map(l => <option key={l.label}>{l.label}</option>)}
        </select>
        {showCohortCol && (
          <select style={s.select} value={filters.cohort} onChange={e => setFilters(f => ({ ...f, cohort: e.target.value }))}>
            <option value="">All Cohorts</option>
            {(adminProfile.companyCodes || []).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <select style={s.select} value={filters.exam} onChange={e => setFilters(f => ({ ...f, exam: e.target.value }))}>
          <option value="">Exam Status</option>
          <option value="passed">Passed</option>
          <option value="inprogress">In Progress</option>
          <option value="nottaken">Not Taken</option>
        </select>
        <select style={s.select} value={filters.post} onChange={e => setFilters(f => ({ ...f, post: e.target.value }))}>
          <option value="">Post Assessment</option>
          <option value="unlocked">Unlocked</option>
          <option value="locked">Locked</option>
        </select>
        <select style={s.select} value={filters.access} onChange={e => setFilters(f => ({ ...f, access: e.target.value }))}>
          <option value="">Access Status</option>
          <option value="active">Active</option>
          <option value="ended">Ended</option>
        </select>
        <input
          style={s.searchInput}
          placeholder="Search by name or email..."
          value={filters.name}
          onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
        />
        <button style={s.exportBtn} onClick={handleExport}>⬇ {selectedCount > 0 ? 'Export Selected (' + selectedCount + ')' : 'Export to Excel'}</button>
      </div>

      {selectedCount > 0 && (
        <div style={{ background: '#F2F0EC', border: '1.5px solid #E8E6E2', borderBottom: 'none', padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#1B2A4A', alignSelf: 'center', minWidth: '120px' }}>{selectedCount} participant{selectedCount !== 1 ? 's' : ''} selected</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A' }}>Change Day Range</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select style={s.smallSelect} value={bulkDayRange.dayStart} onChange={e => setBulkDayRange(b => ({ ...b, dayStart: e.target.value }))}>
                <option value="">Start</option>
                {ALL_DAYS.map(d => <option key={d} value={d}>Day {d}</option>)}
              </select>
              <span style={{ fontSize: '12px', color: '#3D3D3D' }}>to</span>
              <select style={s.smallSelect} value={bulkDayRange.dayEnd} onChange={e => setBulkDayRange(b => ({ ...b, dayEnd: e.target.value }))}>
                <option value="">End</option>
                {ALL_DAYS.map(d => <option key={d} value={d}>Day {d}</option>)}
              </select>
              <button style={s.actionBtn} onClick={applyDayRange} disabled={saving}>Apply</button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A' }}>Post Assessment</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={s.actionBtn} onClick={() => handleBulkUnlockPost(getTargets())} disabled={saving}>Unlock</button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A' }}>Access</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ ...s.actionBtn, background: '#fdecea', color: '#a32d2d', borderColor: '#f5c6c6', border: '1.5px solid' }} onClick={() => handleBulkEndAccess(getTargets())} disabled={saving}>End Access</button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A' }}>Reminders</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={s.actionBtn} onClick={handleSendReminders} disabled={saving}>Send reminder</button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A' }}>Video Access</div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', maxWidth: '320px' }}>
              {ALL_DAYS.map(d => (
                <button
                  key={d}
                  onClick={() => toggleVideoDayChip(d)}
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    padding: '5px 10px',
                    cursor: 'pointer',
                    fontFamily: 'DM Sans, sans-serif',
                    border: '1.5px solid',
                    borderColor: bulkVideoDays.includes(d) ? '#1B2A4A' : '#E8E6E2',
                    background: bulkVideoDays.includes(d) ? '#1B2A4A' : '#fff',
                    color: bulkVideoDays.includes(d) ? '#D4A847' : '#3D3D3D',
                  }}
                >
                  D{d}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={s.actionBtn} onClick={applyVideoAccess} disabled={saving}>Grant</button>
              <button style={{ ...s.actionBtn, background: '#fff', color: '#1B2A4A', border: '1.5px solid #E8E6E2' }} onClick={clearVideoAccess} disabled={saving}>Clear All</button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto', alignSelf: 'center' }}>
            <button style={{ ...s.actionBtn, background: '#1B2A4A', color: '#fff', border: 'none' }} onClick={handleExport} disabled={saving}>⬇ Export Selected</button>
            <button style={{ ...s.actionBtn, background: '#fff', color: '#3D3D3D', border: '1.5px solid #E8E6E2' }} onClick={() => setSelected({})}>Clear</button>
          </div>
        </div>
      )}
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: '36px', padding: '10px 8px' }}>
                <input type="checkbox"
                  checked={filtered.length > 0 && filtered.every(p => selected[p.id])}
                  onChange={toggleSelectAll}
                  style={{ cursor: 'pointer' }}
                />
              </th>
              <SortTh label="Name" sortId="name" />
              <SortTh label="Dept" sortId="dept" />
              <SortTh label="Job Title" sortId="jobTitle" />
              {showCohortCol && <SortTh label="Cohort" sortId="cohort" />}
              <SortTh label="Workbooks" sortId="days" />
              <SortTh label="Pre" sortId="pre" />
              <SortTh label="Post" sortId="post" />
              <SortTh label="Change" sortId="change" />
              <SortTh label="Level" sortId="level" />
              <SortTh label="Exam" sortId="exam" />
              <SortTh label="Videos" sortId="videos" />
              <SortTh label="Access Expires" sortId="accessExpires" />
              <SortTh label="Post Assessment" sortId="postAssessment" />
              <th style={s.th}>Access Controls</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={showCohortCol ? 15 : 14} style={{ ...s.td, textAlign: 'center', color: '#8A8A8A', padding: '32px' }}>
                  No participants match the current filters.
                </td>
              </tr>
            ) : filtered.map(p => {
              const score = p.postScore || p.preScore;
              const level = score ? getLevel(score) : null;
              const levelStyle = level ? (levelColors[level.label] || {}) : {};
              const change = (p.preScore != null && p.postScore != null) ? p.postScore - p.preScore : null;
              return (
                <tr key={p.id} style={{ ...s.tr, background: selected[p.id] ? '#f0f4ff' : 'transparent' }}>
                  <td style={{ ...s.td, padding: '10px 8px' }}>
                    <input type="checkbox" checked={!!selected[p.id]} onChange={() => toggleSelect(p.id)} style={{ cursor: 'pointer' }} />
                  </td>
                  <td style={s.td}>
                    <div style={s.nameCell}>{p.displayName || '—'}</div>
                    <div style={s.emailCell}>{p.email}</div>
                    <div style={s.linkRow}>
                      <button style={s.linkBtn} onClick={() => openEmailEdit(p)}>Edit email</button>
                      <button style={s.linkBtn} onClick={() => handleSendReset(p)} disabled={!!resetBusy[p.id]}>
                        {resetBusy[p.id] ? 'Sending…' : 'Reset password'}
                      </button>
                      <button style={s.linkBtn} onClick={() => openDetailsEdit(p)}>Edit dept / title</button>
                    </div>
                  </td>
                  <td style={s.td}>{p.department || '—'}</td>
                  <td style={s.td}>{p.jobTitle || '—'}</td>
                  {showCohortCol && <td style={s.td}><span style={s.codeBadge}>{p.companyCode}</span></td>}
                  <td style={s.td}>
                    <div style={s.daysBadge}>{p.completedDays} / {p.assignedDays ?? 10}</div>
                    {p.completedDays < (p.assignedDays ?? 10) && p.sectionsCompleted > 0 && (
                      <div style={{ fontSize: '10px', color: '#854f0b', marginTop: '2px', fontWeight: '600' }}>In progress</div>
                    )}
                  </td>
                  <td style={s.td}>{p.preScore != null ? p.preScore : '—'}</td>
                  <td style={s.td}>{p.postScore != null ? p.postScore : '—'}</td>
                  <td style={s.td}>
                    {change != null ? (
                      <span style={{ fontWeight: '600', color: change >= 0 ? '#1a8a4a' : '#C0392B', fontSize: '12px' }}>
                        {change >= 0 ? '+' : ''}{change}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={s.td}>
                    {level && (
                      <span style={{ ...s.levelBadge, ...levelStyle }}>{level.label}</span>
                    )}
                  </td>
                  <td style={s.td}>
                    {(() => {
                      const ex = p.exam || { taken: false };
                      if (!ex.taken) return <span style={{ fontSize: '11px', color: '#8A8A8A' }}>Not taken</span>;
                      if (ex.passed) {
                        return (
                          <span style={{ ...s.unlockedBadge }}>
                            Passed{ex.score != null ? ` · ${ex.score}` : ''}
                          </span>
                        );
                      }
                      return (
                        <span style={{ background: '#fef5e4', color: '#854f0b', padding: '3px 8px', fontSize: '11px', fontWeight: '600', display: 'inline-block' }}>
                          In progress · {ex.attempts} attempt{ex.attempts !== 1 ? 's' : ''}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={s.td}>
                    {(() => {
                      const v = formatVideoAccess(p);
                      return <span style={{ fontSize: '11px', fontWeight: '600', color: v.color }}>{v.label}</span>;
                    })()}
                  </td>
                  <td style={s.td}>
                    <div style={{ color: isAccessActive(p) ? '#1B2A4A' : '#C0392B', fontSize: '12px' }}>
                      {formatDate(p.accessExpiresAt)}
                    </div>
                  </td>
                  <td style={s.td}>
                    {p.postAssessmentUnlocked
                      ? <span style={s.unlockedBadge}>Unlocked</span>
                      : <button style={s.actionBtn} onClick={() => handleUnlockPost(p.id)} disabled={saving}>Unlock</button>}
                  </td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button style={s.actionBtn} onClick={() => handleExtendAccess(p.id, 30)} disabled={saving}>+30 days</button>
                      {isAccessActive(p) && (
                        <button style={{ ...s.actionBtn, background: '#fdecea', color: '#a32d2d', borderColor: '#f5c6c6' }} onClick={() => handleEndAccess(p.id)} disabled={saving}>End</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={s.note}>Workbook responses are private to each participant and not visible here.</div>

      {emailEdit && (
        <div style={s.modalOverlay} onClick={() => !emailEditSaving && setEmailEdit(null)}>
          <div style={s.modalCard} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Edit email</div>
            <div style={s.modalSub}>{emailEdit.displayName || emailEdit.email}</div>
            <input
              style={s.modalInput}
              type="email"
              value={emailEditValue}
              onChange={e => setEmailEditValue(e.target.value)}
              placeholder="new.email@company.com"
            />
            <div style={s.modalNote}>
              This changes the participant's <strong>login email</strong> — they'll sign in with the new address from now on.
            </div>
            <div style={s.modalActions}>
              <button style={s.actionBtn} onClick={() => setEmailEdit(null)} disabled={emailEditSaving}>Cancel</button>
              <button style={s.modalSaveBtn} onClick={handleSaveEmail} disabled={emailEditSaving}>
                {emailEditSaving ? 'Saving…' : 'Save email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailsEdit && (
        <div style={s.modalOverlay} onClick={() => !detailsEditSaving && setDetailsEdit(null)}>
          <div style={s.modalCard} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Edit department and job title</div>
            <div style={s.modalSub}>{detailsEdit.displayName || detailsEdit.email}</div>
            <div style={s.modalField}>
              <label style={s.modalLabel}>Department</label>
              <input
                style={s.modalInput}
                type="text"
                value={detailsEditValue.department}
                onChange={e => setDetailsEditValue(v => ({ ...v, department: e.target.value }))}
                placeholder="e.g. HR, Marketing, Finance"
              />
            </div>
            <div style={s.modalField}>
              <label style={s.modalLabel}>Job Title</label>
              <input
                style={s.modalInput}
                type="text"
                value={detailsEditValue.jobTitle}
                onChange={e => setDetailsEditValue(v => ({ ...v, jobTitle: e.target.value }))}
                placeholder="e.g. HR Manager"
              />
            </div>
            <div style={s.modalNote}>
              The participant can also change these on their own Account Settings page.
            </div>
            <div style={s.modalActions}>
              <button style={s.actionBtn} onClick={() => setDetailsEdit(null)} disabled={detailsEditSaving}>Cancel</button>
              <button style={s.modalSaveBtn} onClick={handleSaveDetails} disabled={detailsEditSaving}>
                {detailsEditSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrapper: { fontFamily: 'DM Sans, sans-serif' },
  loading: { padding: '40px', textAlign: 'center', color: '#8A8A8A' },
  error: { padding: '24px', background: '#fdecea', color: '#a32d2d', fontSize: '14px' },
  heading: { fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: '#1B2A4A', marginBottom: '4px' },
  sub: { fontSize: '13px', color: '#3D3D3D', marginBottom: '24px', fontWeight: '300' },
  msgBox: { fontSize: '12px', padding: '10px 16px', marginBottom: '16px' },
  msgSuccess: { background: '#eaf3de', color: '#3b6d11' },
  msgError: { background: '#fdecea', color: '#a32d2d' },
  statsStrip: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' },
  statCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '16px 20px' },
  statLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#1B2A4A', marginBottom: '6px' },
  statVal: { fontFamily: 'Playfair Display, serif', fontSize: '28px', fontWeight: '900', color: '#1B2A4A' },
  statSub: { fontSize: '11px', color: '#3D3D3D', marginTop: '2px' },
  journeyCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '20px' },
  journeyTitle: { fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#1B2A4A', marginBottom: '16px' },
  journeyRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' },
  journeyLabel: { width: '170px', fontSize: '12px', color: '#3D3D3D', flexShrink: 0 },
  journeyTrack: { flex: 1, background: '#F2F0EC', height: '24px', borderRadius: '3px', overflow: 'hidden' },
  journeyFill: { height: '100%', borderRadius: '3px', transition: 'width 0.5s ease-out' },
  journeyCount: { width: '78px', textAlign: 'right', fontSize: '14px', fontWeight: '700', color: '#1B2A4A', flexShrink: 0 },
  journeyPct: { fontSize: '11px', color: '#8A8A8A', fontWeight: '400' },
  roiPanel: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '20px' },
  roiHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' },
  rateBox: { display: 'flex', alignItems: 'center', gap: '6px' },
  rateLabel: { fontSize: '11px', color: '#8A8A8A', fontWeight: '600' },
  rateInput: { width: '70px', border: '1.5px solid #E8E6E2', padding: '6px 8px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#fff', outline: 'none' },
  rateSaveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '6px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  roiStats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '12px' },
  roiStat: { background: '#F2F0EC', padding: '14px 16px' },
  roiStatNum: { fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: '#1B2A4A' },
  roiStatLabel: { fontSize: '11px', color: '#8A8A8A', marginTop: '2px' },
  roiNote: { fontSize: '11px', color: '#8A8A8A', fontStyle: 'italic', marginBottom: '12px' },
  roiCats: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' },
  roiCatBadge: { fontSize: '11px', fontWeight: '600', background: '#F2F0EC', color: '#1B2A4A', padding: '4px 10px', borderRadius: '3px' },
  roiHighlights: { borderTop: '1px solid #E8E6E2', paddingTop: '12px' },
  roiHighlightsLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '8px' },
  roiHighlight: { display: 'flex', gap: '10px', alignItems: 'baseline', marginBottom: '6px' },
  roiHighlightHours: { fontSize: '11px', fontWeight: '700', color: '#D4A847', background: '#1B2A4A', padding: '2px 8px', whiteSpace: 'nowrap', flexShrink: 0 },
  roiHighlightText: { fontSize: '12px', color: '#3D3D3D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  engBar: { background: '#F2F0EC', border: '1.5px solid #E8E6E2', padding: '10px 20px', display: 'flex', gap: '28px', flexWrap: 'wrap', marginBottom: '4px' },
  engField: { display: 'flex', flexDirection: 'column', gap: '2px' },
  engLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#8A8A8A' },
  engValue: { fontSize: '13px', fontWeight: '500', color: '#1B2A4A' },
  filterBar: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '16px 20px', display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' },
  select: { border: '1.5px solid #E8E6E2', padding: '8px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', minWidth: '130px' },
  searchInput: { border: '1.5px solid #E8E6E2', padding: '8px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FFFFFF', outline: 'none', flex: 1, minWidth: '180px' },
  exportBtn: { background: '#1B2A4A', color: '#FFFFFF', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', marginLeft: 'auto' },
  tableWrap: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: { textAlign: 'left', fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#1B2A4A', padding: '10px 12px', borderBottom: '2px solid #E8E6E2', background: '#F2F0EC', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #E8E6E2' },
  td: { padding: '10px 12px', color: '#3D3D3D', verticalAlign: 'middle' },
  nameCell: { fontWeight: '500', color: '#1B2A4A', fontSize: '13px' },
  emailCell: { fontSize: '11px', color: '#8A8A8A', marginTop: '2px' },
  codeBadge: { background: '#F2F0EC', color: '#1B2A4A', padding: '2px 8px', fontSize: '11px', fontWeight: '600', fontFamily: 'monospace' },
  daysBadge: { background: '#F2F0EC', color: '#1B2A4A', padding: '3px 8px', fontSize: '11px', fontWeight: '600', display: 'inline-block' },
  levelBadge: { fontSize: '10px', fontWeight: '600', padding: '3px 8px', display: 'inline-block' },
  unlockedBadge: { background: '#eaf3de', color: '#3b6d11', padding: '3px 8px', fontSize: '11px', fontWeight: '600', display: 'inline-block' },
  actionBtn: { background: '#F2F0EC', color: '#1B2A4A', border: '1.5px solid #E8E6E2', padding: '4px 10px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  linkBtn: { background: 'none', border: 'none', padding: 0, marginTop: '3px', color: '#185fa5', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', textDecoration: 'underline' },
  linkRow: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' },
  modalLabel: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '6px', display: 'block' },
  modalField: { marginBottom: '14px' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(27,42,74,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modalCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', width: '420px', maxWidth: '100%' },
  modalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
  modalSub: { fontSize: '12px', color: '#8A8A8A', marginBottom: '16px' },
  modalInput: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#3D3D3D', background: '#fff', outline: 'none', boxSizing: 'border-box' },
  modalNote: { fontSize: '12px', color: '#3D3D3D', marginTop: '12px', lineHeight: '1.5' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' },
  modalSaveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '9px 20px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  note: { marginTop: '12px', fontSize: '11px', color: '#8A8A8A', fontStyle: 'italic', padding: '0 4px' },
  smallSelect: { border: '1.5px solid #E8E6E2', padding: '6px 10px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#fff', outline: 'none' },
};
